import { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import InteractiveMap from "../components/InteractiveMap";
import LotDetailDialog from "../components/LotDetailDialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { LayoutDashboard, LogOut, Clock, TrendingUp, Layers, Users, CircleSlash } from "lucide-react";
import VendorsManagement from "../components/admin/VendorsManagement";

// NUEVOS IMPORTES MODULARES
import StatBox from "../components/admin/StatBox";
import ReservationsTable from "../components/admin/ReservationsTable";
import InventoryTable from "../components/admin/InventoryTable";
import EditLotDialog from "../components/admin/EditLotDialog";
import ReservationDetailDialog from "../components/admin/ReservationDetailDialog";
import ReservationEditDialog from "../components/admin/ReservationEditDialog";

export default function AdminDashboardPage() {
  const { user, ready, logout } = useAuth();
  const [lots, setLots] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  
  const [editLot, setEditLot] = useState(null);
  const [reserveLot, setReserveLot] = useState(null);
  const [viewReservation, setViewReservation] = useState(null);
  const [editReservation, setEditReservation] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, r, s] = await Promise.all([
        api.get("/lots"),
        api.get("/reservations"),
        api.get("/stats"),
      ]);
      setLots(l.data); 
      setReservations(
        r.data.map((res) => ({
          ...res,
          id: res.id || res.reservation_id?.toString(),
          id_lots: res.id_lots || res.lot_id,
          lot_label: res.lot_label || `Lote ${res.id_lots || res.lot_id}`,
          status: res.status || "pending",
          created_at: typeof res.created_at === "string" ? res.created_at : res.created_at?.date,
          updated_at: typeof res.updated_at === "string" ? res.updated_at : res.updated_at?.date,
        }))
      );
      setStats(s.data);
    } catch (err) {
      toast.error("Error al cargar datos", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => {
    if (ready && user) load();
  }, [ready, user, load]);

  if (!ready) return null;

  const userRole = user?.role || "vendedor";

  const updateLotStatus = async (lot, status) => {
    try {
      await api.patch(`/lots/${lot.id}`, { status });
      toast.success(`Lote ${lot.id} actualizado`);
      load();
    } catch (err) { toast.error("Error", { description: formatApiError(err) }); }
  };

  const deleteReservation = async (id) => {
    if (!confirm("¿Seguro de cancelar/eliminar esta reservación?")) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Reserva eliminada correctamente");
      load();
    } catch (err) { toast.error("Error", { description: formatApiError(err) }); }
  };

  const approveReservation = async (id) => {
    if (!confirm("¿Seguro de aprobar esta reserva? El lote será 'Vendido'.")) return;
    try {
      await api.patch(`/reservations/${id}`, { status: "approved" });
      toast.success("Reserva aprobada");
      load();
    } catch (err) { toast.error("Error", { description: formatApiError(err) }); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
              <LayoutDashboard size={12} /> Panel de Control
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3 flex-wrap">
              Hola, {user.email}
              <span className={`text-md px-3 py-1 rounded-full font-medium tracking-wide ${userRole === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                {userRole === 'admin' ? 'Administrador' : 'Vendedor'}
              </span>
            </h1>
          </div>
          <Button variant="outline" onClick={logout} className="inline-flex items-center gap-2">
            <LogOut size={14} /> Cerrar sesión
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            <StatBox icon={<Layers size={16} />} label="Total lotes" value={stats.total_lots} />
            <StatBox icon={<TrendingUp size={16} />} label="Disponibles" value={stats.available} dot="bg-emerald-500" />
            <StatBox icon={<Clock size={16} />} label="Reservados" value={stats.reserved} dot="bg-amber-500" />
            <StatBox icon={<CircleSlash size={16} />} label="Vendidos" value={stats.sold} dot="bg-red-500" />
            <StatBox icon={<Users size={16} />} label="Solicitudes pendientes" value={stats.pending_reservations} />
          </div>
        )}

        <Tabs defaultValue="reservations" className="w-full">
          <TabsList>
            <TabsTrigger value="reservations">{userRole === 'admin' ? 'Todas las Reservas' : 'Mis Reservas'}</TabsTrigger>
            <TabsTrigger value="inventory">Estado de Lotes</TabsTrigger>
            <TabsTrigger value="map">Mapa de Reservas</TabsTrigger>
            {/* PESTAÑA SOLO VISIBLE PARA EL ADMINISTRADOR */}
            {userRole === 'admin' && (
              <TabsTrigger value="vendors">Administrar Vendedores</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="reservations" className="mt-6">
            <ReservationsTable items={reservations} userRole={userRole} onView={setViewReservation} onEdit={setEditReservation} onDelete={deleteReservation} onApprove={approveReservation} />
          </TabsContent>

          <TabsContent value="inventory" className="mt-6">
            <InventoryTable items={lots} userRole={userRole} onStatus={updateLotStatus} onEdit={setEditLot} />
          </TabsContent>

          <TabsContent value="map" className="mt-6">
            {/* <InteractiveMap lots={lots} onLotClick={(lot) => userRole === "admin" ? setEditLot(lot) : setReserveLot(lot)} /> */}
              <InteractiveMap lots={lots} onLotClick={(lot) => setReserveLot(lot)} />
          </TabsContent>
          
          {/* CONTENIDO DE LA PESTAÑA SOLO VISIBLE PARA EL ADMINISTRADOR */}
          {userRole === 'admin' && (
            <TabsContent value="vendors" className="mt-6">
              <VendorsManagement />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <EditLotDialog lot={editLot} onClose={() => setEditLot(null)} onSaved={() => { setEditLot(null); load(); }} />
      <LotDetailDialog open={!!reserveLot} onOpenChange={(isOpen) => !isOpen && setReserveLot(null)} lot={reserveLot} viewOnly={false} onReserved={() => { setReserveLot(null); load(); }} />
      <ReservationDetailDialog reservation={viewReservation} onClose={() => setViewReservation(null)} />
      <ReservationEditDialog reservation={editReservation} onClose={() => setEditReservation(null)} onSaved={() => { setEditReservation(null); load(); }} />
    </div>
  );
}